import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatName } from '@/lib/utils';
import { Save, Plus, X, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// MSE categories — `Other` is appended automatically by ObservationSection
const MSE_CATEGORIES = [
  { key: 'general_appearance', label: 'General Appearance', options: ['Well-groomed', 'Unkempt', 'Disheveled', 'Bizarre', 'Age-appropriate'] },
  { key: 'posture_motor', label: 'Posture & Motor Activity', options: ['Normal', 'Restless', 'Agitated', 'Retarded', 'Tremors', 'Tics'] },
  { key: 'eye_contact', label: 'Eye Contact', options: ['Normal', 'Avoidant', 'Intense', 'Fleeting'] },
  { key: 'speech', label: 'Speech', options: ['Normal', 'Pressured', 'Slow', 'Loud', 'Soft', 'Slurred', 'Mute'] },
  { key: 'mood', label: 'Mood (Self-reported)', options: ['Euthymic', 'Depressed', 'Anxious', 'Irritable', 'Euphoric', 'Fearful', 'Angry'] },
  { key: 'affect', label: 'Affect (Observed)', options: ['Appropriate', 'Flat', 'Blunted', 'Labile', 'Restricted', 'Incongruent'] },
  { key: 'thought_process', label: 'Thought Process', options: ['Logical', 'Circumstantial', 'Tangential', 'Loosening of associations', 'Flight of ideas', 'Perseveration', 'Thought blocking'] },
  { key: 'thought_content', label: 'Thought Content', options: ['Normal', 'Suicidal ideation', 'Homicidal ideation', 'Delusions', 'Obsessions', 'Phobias', 'Ideas of reference'] },
  { key: 'perception', label: 'Perception', options: ['Normal', 'Auditory hallucinations', 'Visual hallucinations', 'Illusions', 'Depersonalization', 'Derealization'] },
  { key: 'orientation', label: 'Orientation', options: ['Oriented to time/place/person', 'Disoriented to time', 'Disoriented to place', 'Disoriented to person'] },
  { key: 'memory', label: 'Memory', options: ['Intact', 'Impaired (immediate)', 'Impaired (recent)', 'Impaired (remote)'] },
  { key: 'attention_concentration', label: 'Attention & Concentration', options: ['Good', 'Fair', 'Poor'] },
  { key: 'judgment', label: 'Judgment', options: ['Good', 'Fair', 'Poor'] },
  { key: 'insight', label: 'Insight', options: ['Good', 'Partial', 'Poor'] },
];

// Normalize MSE category value across legacy formats:
//   undefined         -> { values: [], other: '' }
//   "Anxious"         -> { values: ['Anxious'], other: '' }            (legacy single-select)
//   ['Anxious']       -> { values: ['Anxious'], other: '' }            (legacy array)
//   { values, other } -> { values, other }                              (new shape)
function getMseCat(obs, key) {
  const raw = obs?.[key];
  if (raw == null || raw === '') return { values: [], other: '' };
  if (typeof raw === 'string') return { values: [raw], other: '' };
  if (Array.isArray(raw)) return { values: raw, other: '' };
  return { values: Array.isArray(raw.values) ? raw.values : [], other: raw.other || '' };
}

// Run "Other text required" validation; returns map cat.key -> error string for invalid cats.
function validateMse(obs) {
  const errs = {};
  for (const cat of MSE_CATEGORIES) {
    const { values, other } = getMseCat(obs, cat.key);
    if (values.includes('Other') && !other.trim()) {
      errs[cat.key] = 'Please specify';
    }
  }
  return errs;
}

function SectionWrapper({ title, sectionKey, meta, isOpen, onToggle, children, onSave, saving }) {
  return (
    <div className="border border-[#E5F2F1] rounded-2xl overflow-hidden" data-testid={`section-${sectionKey}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between p-4 bg-[#F4FAF9] hover:bg-[#E5F2F1] transition-colors text-left" data-testid={`section-toggle-${sectionKey}`}>
        <div className="flex items-center gap-2">
          {meta ? <Check className="w-4 h-4 text-[#2E8B57]" /> : <div className="w-4 h-4 rounded-full border-2 border-[#4A6B68]/30" />}
          <span className="text-sm font-semibold text-[#1A3330] font-['Outfit']">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-4 h-4 text-[#4A6B68]" /> : <ChevronRight className="w-4 h-4 text-[#4A6B68]" />}
      </button>
      {isOpen && (
        <div className="p-4 space-y-4 bg-white">
          {children}
          {meta?.last_updated_at && (
            <p className="text-[10px] text-[#4A6B68] italic">
              Last updated by {meta.updated_by_name || 'Unknown'} on {new Date(meta.last_updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          <Button onClick={onSave} disabled={saving} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl text-xs h-8 px-4" data-testid={`save-${sectionKey}`}>
            <Save className="w-3.5 h-3.5 mr-1.5" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}

function ObservationSection({ observation, setObservation, customObservations, setCustomObservations, mseErrors }) {
  const toggleOption = (catKey, opt) => {
    const cur = getMseCat(observation, catKey);
    const has = cur.values.includes(opt);
    const nextValues = has ? cur.values.filter(v => v !== opt) : [...cur.values, opt];
    const nextOther = !has || opt !== 'Other' ? cur.other : ''; // clear text when unchecking Other
    setObservation(prev => ({ ...prev, [catKey]: { values: nextValues, other: nextOther } }));
  };
  const setOtherText = (catKey, val) => {
    const cur = getMseCat(observation, catKey);
    setObservation(prev => ({ ...prev, [catKey]: { ...cur, other: val } }));
  };
  return (
    <div className="space-y-3">
      {MSE_CATEGORIES.map(cat => {
        const { values, other } = getMseCat(observation, cat.key);
        const otherChecked = values.includes('Other');
        const err = mseErrors?.[cat.key];
        const allOpts = [...cat.options, 'Other'];
        return (
          <div key={cat.key} className="border-b border-[#E5F2F1] pb-3 last:border-0" data-testid={`mse-${cat.key}`}>
            <Label className="text-xs font-medium text-[#1A3330] mb-1.5 block">{cat.label}</Label>
            <div className="flex flex-wrap gap-1.5">
              {allOpts.map(opt => {
                const checked = values.includes(opt);
                const tid = `mse-${cat.key}-${opt.replace(/\s+/g, '-').toLowerCase()}`;
                return (
                  <button
                    key={opt}
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    onClick={() => toggleOption(cat.key, opt)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] border transition-all inline-flex items-center gap-1.5 ${
                      checked
                        ? 'bg-[#0D8B7D] text-white border-[#0D8B7D]'
                        : 'bg-white text-[#1A3330] border-[#E5F2F1] hover:border-[#0D8B7D]/40'
                    }`}
                    data-testid={tid}
                  >
                    <span
                      aria-hidden="true"
                      className={`w-3 h-3 rounded-[3px] border flex items-center justify-center text-white ${
                        checked ? 'bg-white/20 border-white' : 'border-[#4A6B68]/40 bg-white'
                      }`}
                    >
                      {checked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
            {otherChecked && (
              <div className="mt-2" data-testid={`mse-${cat.key}-other-wrap`}>
                <Input
                  value={other}
                  onChange={e => setOtherText(cat.key, e.target.value)}
                  placeholder={`Specify other ${cat.label.toLowerCase()}…`}
                  className={`rounded-xl h-8 text-xs ${err ? 'border-[#D3544E] focus-visible:ring-[#D3544E]/30' : 'border-[#E5F2F1]'}`}
                  data-testid={`mse-${cat.key}-other-input`}
                />
                {err && (
                  <p className="text-[10px] text-[#D3544E] mt-1" data-testid={`mse-${cat.key}-other-error`}>
                    {err}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div className="border-t border-[#E5F2F1] pt-3">
        <Label className="text-xs font-medium text-[#1A3330] mb-2 block">Additional Observations</Label>
        {customObservations.map((obs, idx) => (
          <div key={idx} className="flex items-center gap-2 mb-2" data-testid={`custom-obs-${idx}`}>
            <Input value={obs.label} onChange={e => { const u = [...customObservations]; u[idx] = { ...u[idx], label: e.target.value }; setCustomObservations(u); }} placeholder="Category name" className="rounded-xl border-[#E5F2F1] h-8 text-xs flex-1" />
            <Input value={obs.value} onChange={e => { const u = [...customObservations]; u[idx] = { ...u[idx], value: e.target.value }; setCustomObservations(u); }} placeholder="Observation value" className="rounded-xl border-[#E5F2F1] h-8 text-xs flex-1" />
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setCustomObservations(prev => prev.filter((_, i) => i !== idx))} data-testid={`delete-custom-obs-${idx}`}><X className="w-3.5 h-3.5 text-[#D3544E]" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className="rounded-xl border-[#E5F2F1] text-[#0D8B7D] text-xs h-8" onClick={() => setCustomObservations(prev => [...prev, { label: '', value: '' }])} data-testid="add-custom-observation-btn">
          <Plus className="w-3.5 h-3.5 mr-1" />Add More Observations
        </Button>
      </div>
    </div>
  );
}

const ADULT_HISTORY_SUBS = [
  { key: 'personal', label: 'Personal History' }, { key: 'family', label: 'Family History' },
  { key: 'medical', label: 'Medical History' }, { key: 'birth', label: 'Birth History' },
  { key: 'relationship', label: 'Relationship History' }, { key: 'substance', label: 'Substance Use' },
];

const CHILD_HISTORY_SUBS = ['personal', 'family', 'medical', 'psychosexual', 'substance'];

export default function CaseHistoryTab({ clientId, clientType, linkedPartner, clientName }) {
  const [sections, setSections] = useState({});
  const [status, setStatus] = useState(null);
  const [openSection, setOpenSection] = useState(null);
  const [saving, setSaving] = useState('');

  // Adult / Child state
  const [presentingComplaints, setPresentingComplaints] = useState('');
  const [history, setHistory] = useState({});
  const [childBirth, setChildBirth] = useState({ antenatal: '', delivery_type: '', birth_weight: '', birth_cry: '', neonatal: '', immunisation: '', reflexes: '', speech: '', walking: '', toilet_training: '', postnatal_other: '', childhood_behaviour: '', home_env: '', school_enrolment: '', peer_relationships: '' });
  const [observation, setObservation] = useState({});
  const [customObservations, setCustomObservations] = useState([]);
  const [mseErrors, setMseErrors] = useState({});
  const [provisionalDiagnosis, setProvisionalDiagnosis] = useState('');
  const [differentialDiagnosis, setDifferentialDiagnosis] = useState('');

  // Relationship state
  const [relDetails, setRelDetails] = useState({ type: '', duration: '', format: '' });
  const [primaryComplaints, setPrimaryComplaints] = useState({ complaints: '', triggers: '' });
  const [relNotes, setRelNotes] = useState('');
  const [coreIssue, setCoreIssue] = useState('');
  const [individualFactors, setIndividualFactors] = useState({ partner1: '', partner2: '' });
  const [formulation, setFormulation] = useState('');
  const [relProvDiag, setRelProvDiag] = useState({ partner1: '', partner2: '' });
  const [relDiffDiag, setRelDiffDiag] = useState({ partner1: '', partner2: '' });

  const ct = clientType || 'ADULT';
  const isRelationship = ct === 'RELATIONSHIP';
  const p1Name = clientName || 'Partner 1';
  const p2Name = linkedPartner ? formatName(linkedPartner) : 'Partner 2';

  const fetchData = useCallback(async () => {
    try {
      const [chRes, stRes] = await Promise.all([
        api.get(`/case-history/${clientId}`),
        api.get(`/case-history/${clientId}/status`),
      ]);
      const s = chRes.data.sections || {};
      setSections(s);
      setStatus(stRes.data);

      // Populate from saved data
      if (s.presenting_complaints?.data) setPresentingComplaints(s.presenting_complaints.data);
      if (s.history?.data) {
        const hd = s.history.data;
        if (typeof hd === 'object') {
          setHistory(prev => ({ ...prev, ...hd }));
          if (hd.child_birth) setChildBirth(prev => ({ ...prev, ...hd.child_birth }));
        }
      }
      if (s.observation?.data) {
        setObservation(s.observation.data.mse || {});
        setCustomObservations(s.observation.data.custom || []);
      }
      if (s.provisional_diagnosis?.data) {
        const pd = s.provisional_diagnosis.data;
        if (typeof pd === 'object') setRelProvDiag(prev => ({ ...prev, ...pd }));
        else setProvisionalDiagnosis(pd);
      }
      if (s.differential_diagnosis?.data) {
        const dd = s.differential_diagnosis.data;
        if (typeof dd === 'object') setRelDiffDiag(prev => ({ ...prev, ...dd }));
        else setDifferentialDiagnosis(dd);
      }
      // Relationship-specific
      if (s.relationship_details?.data) setRelDetails(prev => ({ ...prev, ...s.relationship_details.data }));
      if (s.primary_complaints?.data) setPrimaryComplaints(prev => ({ ...prev, ...s.primary_complaints.data }));
      if (s.notes?.data) setRelNotes(s.notes.data);
      if (s.core_issue?.data) setCoreIssue(s.core_issue.data);
      if (s.individual_factors?.data) setIndividualFactors(prev => ({ ...prev, ...s.individual_factors.data }));
      if (s.formulation?.data) setFormulation(s.formulation.data);
    } catch {}
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveSection = async (sectionKey, data) => {
    if (sectionKey === 'observation') {
      const errs = validateMse(data?.mse || {});
      setMseErrors(errs);
      if (Object.keys(errs).length > 0) return;
    }
    setSaving(sectionKey);
    try {
      await api.put(`/case-history/${clientId}`, { section: sectionKey, data });
      await fetchData();
    } catch {} finally { setSaving(''); }
  };

  const totalSections = status?.total_sections || (isRelationship ? 8 : 5);
  const completedSections = status?.completed_sections || 0;
  const progressPercent = (completedSections / totalSections) * 100;

  const toggle = (key) => setOpenSection(openSection === key ? null : key);

  if (isRelationship) {
    return (
      <div className="space-y-4" data-testid="case-history-tab">
        <Card className="bg-white border-[#E5F2F1] rounded-2xl"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#1A3330] font-['Outfit']">Case History Progress</span>
            <span className="text-xs text-[#4A6B68]" data-testid="case-history-progress-text">{completedSections} of {totalSections} sections completed</span>
          </div>
          <Progress value={progressPercent} className="h-2 [&>div]:bg-[#0D8B7D]" />
        </CardContent></Card>

        {/* A — Relationship Details */}
        <SectionWrapper title="A. Relationship Details" sectionKey="relationship_details" meta={sections.relationship_details} isOpen={openSection === 'relationship_details'} onToggle={() => toggle('relationship_details')} onSave={() => saveSection('relationship_details', relDetails)} saving={saving === 'relationship_details'}>
          <div className="space-y-3">
            <div><Label className="text-xs">Relationship Type</Label>
              <Select value={relDetails.type} onValueChange={v => setRelDetails(p => ({ ...p, type: v }))}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="rel-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {['Married', 'Premarital', 'Live-in', 'Separated', 'Divorced', 'Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Duration of Relationship</Label><Input value={relDetails.duration} onChange={e => setRelDetails(p => ({ ...p, duration: e.target.value }))} placeholder="e.g. 3 years 2 months" className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="rel-duration-input" /></div>
            <div><Label className="text-xs">Presenting Format</Label>
              <div className="flex gap-2 mt-1">
                {['Joint Sessions', 'Individual Sessions', 'Both'].map(f => (
                  <button key={f} type="button" onClick={() => setRelDetails(p => ({ ...p, format: f }))}
                    className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${relDetails.format === f ? 'bg-[#0D8B7D] text-white border-[#0D8B7D]' : 'bg-white text-[#1A3330] border-[#E5F2F1]'}`}
                    data-testid={`rel-format-${f.replace(/\s+/g, '-').toLowerCase()}`}
                  >{f}</button>
                ))}
              </div>
            </div>
          </div>
        </SectionWrapper>

        {/* B — Primary Complaints */}
        <SectionWrapper title="B. Primary Complaints" sectionKey="primary_complaints" meta={sections.primary_complaints} isOpen={openSection === 'primary_complaints'} onToggle={() => toggle('primary_complaints')} onSave={() => saveSection('primary_complaints', primaryComplaints)} saving={saving === 'primary_complaints'}>
          <div className="space-y-3">
            <div><Label className="text-xs">Primary Complaints</Label><Textarea value={primaryComplaints.complaints} onChange={e => setPrimaryComplaints(p => ({ ...p, complaints: e.target.value }))} rows={4} className="rounded-xl border-[#E5F2F1]" placeholder="Describe primary complaints..." data-testid="primary-complaints-input" /></div>
            <div><Label className="text-xs">Recent Triggers</Label><Textarea value={primaryComplaints.triggers} onChange={e => setPrimaryComplaints(p => ({ ...p, triggers: e.target.value }))} rows={3} className="rounded-xl border-[#E5F2F1]" placeholder="Describe recent triggers..." data-testid="recent-triggers-input" /></div>
          </div>
        </SectionWrapper>

        {/* C — Notes */}
        <SectionWrapper title="C. Notes" sectionKey="notes" meta={sections.notes} isOpen={openSection === 'notes'} onToggle={() => toggle('notes')} onSave={() => saveSection('notes', relNotes)} saving={saving === 'notes'}>
          <Textarea value={relNotes} onChange={e => setRelNotes(e.target.value)} rows={5} className="rounded-xl border-[#E5F2F1]" placeholder="General relationship case notes..." data-testid="rel-notes-input" />
        </SectionWrapper>

        {/* D — Core Issue */}
        <SectionWrapper title="D. Core Issue" sectionKey="core_issue" meta={sections.core_issue} isOpen={openSection === 'core_issue'} onToggle={() => toggle('core_issue')} onSave={() => saveSection('core_issue', coreIssue)} saving={saving === 'core_issue'}>
          <Textarea value={coreIssue} onChange={e => setCoreIssue(e.target.value)} rows={4} className="rounded-xl border-[#E5F2F1]" placeholder="Describe core issue..." data-testid="core-issue-input" />
        </SectionWrapper>

        {/* E — Individual Factors */}
        <SectionWrapper title="E. Individual Factors" sectionKey="individual_factors" meta={sections.individual_factors} isOpen={openSection === 'individual_factors'} onToggle={() => toggle('individual_factors')} onSave={() => saveSection('individual_factors', individualFactors)} saving={saving === 'individual_factors'}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="text-xs font-medium text-[#0D8B7D]">{p1Name}</Label><Textarea value={individualFactors.partner1} onChange={e => setIndividualFactors(p => ({ ...p, partner1: e.target.value }))} rows={4} className="rounded-xl border-[#E5F2F1] mt-1" placeholder={`Factors for ${p1Name}...`} data-testid="individual-factors-p1" /></div>
            <div><Label className="text-xs font-medium text-[#F5B041]">{p2Name}</Label><Textarea value={individualFactors.partner2} onChange={e => setIndividualFactors(p => ({ ...p, partner2: e.target.value }))} rows={4} className="rounded-xl border-[#E5F2F1] mt-1" placeholder={`Factors for ${p2Name}...`} data-testid="individual-factors-p2" /></div>
          </div>
        </SectionWrapper>

        {/* F — Formulation */}
        <SectionWrapper title="F. Formulation" sectionKey="formulation" meta={sections.formulation} isOpen={openSection === 'formulation'} onToggle={() => toggle('formulation')} onSave={() => saveSection('formulation', formulation)} saving={saving === 'formulation'}>
          <div>
            <Textarea value={formulation} onChange={e => setFormulation(e.target.value)} rows={4} className="rounded-xl border-[#E5F2F1]" placeholder="Enter formulation..." data-testid="formulation-input" />
            <p className="text-[10px] text-[#4A6B68] mt-1">This will appear on the patient list card as a formulation label.</p>
          </div>
        </SectionWrapper>

        {/* G — Provisional Diagnosis */}
        <SectionWrapper title="G. Provisional Diagnosis" sectionKey="provisional_diagnosis" meta={sections.provisional_diagnosis} isOpen={openSection === 'provisional_diagnosis'} onToggle={() => toggle('provisional_diagnosis')} onSave={() => saveSection('provisional_diagnosis', relProvDiag)} saving={saving === 'provisional_diagnosis'}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="text-xs font-medium text-[#0D8B7D]">{p1Name} Provisional Diagnosis</Label><Textarea value={relProvDiag.partner1} onChange={e => setRelProvDiag(p => ({ ...p, partner1: e.target.value }))} rows={3} className="rounded-xl border-[#E5F2F1] mt-1" data-testid="prov-diag-p1" /></div>
            <div><Label className="text-xs font-medium text-[#F5B041]">{p2Name} Provisional Diagnosis</Label><Textarea value={relProvDiag.partner2} onChange={e => setRelProvDiag(p => ({ ...p, partner2: e.target.value }))} rows={3} className="rounded-xl border-[#E5F2F1] mt-1" data-testid="prov-diag-p2" /></div>
          </div>
        </SectionWrapper>

        {/* H — Differential Diagnosis */}
        <SectionWrapper title="H. Differential Diagnosis" sectionKey="differential_diagnosis" meta={sections.differential_diagnosis} isOpen={openSection === 'differential_diagnosis'} onToggle={() => toggle('differential_diagnosis')} onSave={() => saveSection('differential_diagnosis', relDiffDiag)} saving={saving === 'differential_diagnosis'}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="text-xs font-medium text-[#0D8B7D]">{p1Name} Differential Diagnosis</Label><Textarea value={relDiffDiag.partner1} onChange={e => setRelDiffDiag(p => ({ ...p, partner1: e.target.value }))} rows={3} className="rounded-xl border-[#E5F2F1] mt-1" data-testid="diff-diag-p1" /></div>
            <div><Label className="text-xs font-medium text-[#F5B041]">{p2Name} Differential Diagnosis</Label><Textarea value={relDiffDiag.partner2} onChange={e => setRelDiffDiag(p => ({ ...p, partner2: e.target.value }))} rows={3} className="rounded-xl border-[#E5F2F1] mt-1" data-testid="diff-diag-p2" /></div>
          </div>
        </SectionWrapper>
      </div>
    );
  }

  // === ADULT / CHILD ===
  const isChild = ct === 'CHILD';
  const historySubs = isChild ? CHILD_HISTORY_SUBS : ADULT_HISTORY_SUBS;

  const buildHistoryData = () => {
    const base = {};
    if (isChild) {
      CHILD_HISTORY_SUBS.forEach(k => { base[k] = history[k] || ''; });
      base.child_birth = childBirth;
    } else {
      ADULT_HISTORY_SUBS.forEach(s => { base[s.key] = history[s.key] || ''; });
    }
    return base;
  };

  return (
    <div className="space-y-4" data-testid="case-history-tab">
      <Card className="bg-white border-[#E5F2F1] rounded-2xl"><CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[#1A3330] font-['Outfit']">Case History Progress</span>
          <span className="text-xs text-[#4A6B68]" data-testid="case-history-progress-text">{completedSections} of {totalSections} sections completed</span>
        </div>
        <Progress value={progressPercent} className="h-2 [&>div]:bg-[#0D8B7D]" />
      </CardContent></Card>

      {/* A — Presenting Complaints */}
      <SectionWrapper title="A. Presenting Complaints" sectionKey="presenting_complaints" meta={sections.presenting_complaints} isOpen={openSection === 'presenting_complaints'} onToggle={() => toggle('presenting_complaints')} onSave={() => saveSection('presenting_complaints', presentingComplaints)} saving={saving === 'presenting_complaints'}>
        <Textarea value={presentingComplaints} onChange={e => setPresentingComplaints(e.target.value)} rows={5} className="rounded-xl border-[#E5F2F1]" placeholder="Describe presenting complaints..." data-testid="presenting-complaints-input" />
      </SectionWrapper>

      {/* B — History */}
      <SectionWrapper title="B. History of Presenting Complaints" sectionKey="history" meta={sections.history} isOpen={openSection === 'history'} onToggle={() => toggle('history')} onSave={() => saveSection('history', buildHistoryData())} saving={saving === 'history'}>
        <div className="space-y-4">
          {isChild ? (
            <>
              {/* Child: text sub-sections */}
              {[{ key: 'personal', label: 'Personal History' }, { key: 'family', label: 'Family History' }, { key: 'medical', label: 'Medical History' }].map(sub => (
                <div key={sub.key}><Label className="text-xs font-medium text-[#1A3330]">{sub.label}</Label>
                  <Textarea value={history[sub.key] || ''} onChange={e => setHistory(p => ({ ...p, [sub.key]: e.target.value }))} rows={3} className="rounded-xl border-[#E5F2F1] mt-1" placeholder={`Enter ${sub.label.toLowerCase()}...`} data-testid={`history-${sub.key}-input`} />
                </div>
              ))}

              {/* Birth History (structured) */}
              <div className="border border-[#E5F2F1] rounded-xl p-4 space-y-3">
                <Label className="text-xs font-semibold text-[#1A3330] uppercase tracking-wider">Birth History</Label>
                <div><Label className="text-[11px] text-[#4A6B68]">Antenatal (maternal illness / stress / consanguinity)</Label>
                  <Textarea value={childBirth.antenatal} onChange={e => setChildBirth(p => ({ ...p, antenatal: e.target.value }))} rows={2} className="rounded-xl border-[#E5F2F1] mt-0.5" data-testid="birth-antenatal" /></div>
                <div className="border-l-2 border-[#0D8B7D]/30 pl-3 space-y-2">
                  <p className="text-[11px] font-medium text-[#0D8B7D]">Perinatal</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px]">Delivery type</Label><Input value={childBirth.delivery_type} onChange={e => setChildBirth(p => ({ ...p, delivery_type: e.target.value }))} className="rounded-xl border-[#E5F2F1] h-8 text-xs" data-testid="birth-delivery-type" /></div>
                    <div><Label className="text-[10px]">Birth weight</Label><Input value={childBirth.birth_weight} onChange={e => setChildBirth(p => ({ ...p, birth_weight: e.target.value }))} className="rounded-xl border-[#E5F2F1] h-8 text-xs" data-testid="birth-weight" /></div>
                    <div><Label className="text-[10px]">Birth cry</Label><Input value={childBirth.birth_cry} onChange={e => setChildBirth(p => ({ ...p, birth_cry: e.target.value }))} className="rounded-xl border-[#E5F2F1] h-8 text-xs" data-testid="birth-cry" /></div>
                    <div><Label className="text-[10px]">Neonatal jaundice / congenital abnormalities</Label><Input value={childBirth.neonatal} onChange={e => setChildBirth(p => ({ ...p, neonatal: e.target.value }))} className="rounded-xl border-[#E5F2F1] h-8 text-xs" data-testid="birth-neonatal" /></div>
                  </div>
                </div>
                <div className="border-l-2 border-[#F5B041]/30 pl-3 space-y-2">
                  <p className="text-[11px] font-medium text-[#F5B041]">Postnatal</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[['immunisation', 'Immunisation'], ['reflexes', 'Reflexes'], ['speech', 'Speech'], ['walking', 'Walking'], ['toilet_training', 'Toilet training'], ['postnatal_other', 'Other']].map(([k, l]) => (
                      <div key={k}><Label className="text-[10px]">{l}</Label><Input value={childBirth[k]} onChange={e => setChildBirth(p => ({ ...p, [k]: e.target.value }))} className="rounded-xl border-[#E5F2F1] h-8 text-xs" data-testid={`birth-${k}`} /></div>
                    ))}
                  </div>
                </div>
                <div><Label className="text-[11px] text-[#4A6B68]">Childhood Behaviour Disturbances</Label>
                  <Textarea value={childBirth.childhood_behaviour} onChange={e => setChildBirth(p => ({ ...p, childhood_behaviour: e.target.value }))} rows={2} className="rounded-xl border-[#E5F2F1] mt-0.5" placeholder="Bedwetting, ODD, ADHD, thumb sucking, fire setting, tantrum..." data-testid="birth-childhood-behaviour" /></div>
                <div><Label className="text-[11px] text-[#4A6B68]">Home Environment</Label>
                  <div className="flex gap-2 mt-1">
                    {['Congenial', 'Uncongenial'].map(v => (
                      <button key={v} type="button" onClick={() => setChildBirth(p => ({ ...p, home_env: v }))}
                        className={`px-3 py-1 rounded-lg text-xs border ${childBirth.home_env === v ? 'bg-[#0D8B7D] text-white border-[#0D8B7D]' : 'bg-white text-[#1A3330] border-[#E5F2F1]'}`}
                        data-testid={`birth-home-${v.toLowerCase()}`}
                      >{v}</button>
                    ))}
                  </div>
                </div>
                <div><Label className="text-[11px] text-[#4A6B68]">Scholastic & Extracurricular — Age of school enrolment</Label>
                  <Input value={childBirth.school_enrolment} onChange={e => setChildBirth(p => ({ ...p, school_enrolment: e.target.value }))} className="rounded-xl border-[#E5F2F1] h-8 text-xs mt-0.5" data-testid="birth-school-enrolment" /></div>
                <div><Label className="text-[11px] text-[#4A6B68]">Relationship with Peers / Parents / Siblings</Label>
                  <Textarea value={childBirth.peer_relationships} onChange={e => setChildBirth(p => ({ ...p, peer_relationships: e.target.value }))} rows={2} className="rounded-xl border-[#E5F2F1] mt-0.5" data-testid="birth-peer-relationships" /></div>
              </div>

              {/* Psychosexual & Substance */}
              {[{ key: 'psychosexual', label: 'Psychosexual History' }, { key: 'substance', label: 'Substance Use' }].map(sub => (
                <div key={sub.key}><Label className="text-xs font-medium text-[#1A3330]">{sub.label}</Label>
                  <Textarea value={history[sub.key] || ''} onChange={e => setHistory(p => ({ ...p, [sub.key]: e.target.value }))} rows={3} className="rounded-xl border-[#E5F2F1] mt-1" placeholder={`Enter ${sub.label.toLowerCase()}...`} data-testid={`history-${sub.key}-input`} />
                </div>
              ))}
            </>
          ) : (
            /* Adult history sub-sections */
            ADULT_HISTORY_SUBS.map(sub => (
              <div key={sub.key}><Label className="text-xs font-medium text-[#1A3330]">{sub.label}</Label>
                <Textarea value={history[sub.key] || ''} onChange={e => setHistory(p => ({ ...p, [sub.key]: e.target.value }))} rows={3} className="rounded-xl border-[#E5F2F1] mt-1" placeholder={`Enter ${sub.label.toLowerCase()}...`} data-testid={`history-${sub.key}-input`} />
              </div>
            ))
          )}
        </div>
      </SectionWrapper>

      {/* C — Observation (shared) */}
      <SectionWrapper title="C. Observation (Mental Status Examination)" sectionKey="observation" meta={sections.observation} isOpen={openSection === 'observation'} onToggle={() => toggle('observation')} onSave={() => saveSection('observation', { mse: observation, custom: customObservations })} saving={saving === 'observation'}>
        <ObservationSection observation={observation} setObservation={setObservation} customObservations={customObservations} setCustomObservations={setCustomObservations} mseErrors={mseErrors} />
      </SectionWrapper>

      {/* D — Provisional Diagnosis */}
      <SectionWrapper title="D. Provisional Diagnosis" sectionKey="provisional_diagnosis" meta={sections.provisional_diagnosis} isOpen={openSection === 'provisional_diagnosis'} onToggle={() => toggle('provisional_diagnosis')} onSave={() => saveSection('provisional_diagnosis', provisionalDiagnosis)} saving={saving === 'provisional_diagnosis'}>
        <div>
          <Textarea value={provisionalDiagnosis} onChange={e => setProvisionalDiagnosis(e.target.value)} rows={4} className="rounded-xl border-[#E5F2F1]" placeholder="Enter provisional diagnosis..." data-testid="provisional-diagnosis-input" />
          <p className="text-[10px] text-[#4A6B68] mt-1">This will appear on the patient list card as a provisional diagnosis label.</p>
        </div>
      </SectionWrapper>

      {/* E — Differential Diagnosis */}
      <SectionWrapper title="E. Differential Diagnosis" sectionKey="differential_diagnosis" meta={sections.differential_diagnosis} isOpen={openSection === 'differential_diagnosis'} onToggle={() => toggle('differential_diagnosis')} onSave={() => saveSection('differential_diagnosis', differentialDiagnosis)} saving={saving === 'differential_diagnosis'}>
        <Textarea value={differentialDiagnosis} onChange={e => setDifferentialDiagnosis(e.target.value)} rows={4} className="rounded-xl border-[#E5F2F1]" placeholder="Enter differential diagnoses..." data-testid="differential-diagnosis-input" />
      </SectionWrapper>
    </div>
  );
}
